import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { Client } from '@shared/types';
import { US_STATES } from '@shared/constants';
import { useClientContacts } from '../../hooks/useClientContacts';
import { ContactsList } from './ContactsList';
import { clientService } from '../../services/clientService';

interface ClientEditorProps {
  client: Client | null;
  onSave: (client: Partial<Client>) => void;
  onCancel: () => void;
  saving?: boolean;
}

export function ClientEditor({ client, onSave, onCancel, saving = false }: ClientEditorProps) {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'prospect'>('active');
  const [enriching, setEnriching] = useState(false);

  // Load contacts for existing clients
  const { contacts, reload: reloadContacts } = useClientContacts(client?.id || '');

  useEffect(() => {
    if (client) {
      setName(client.name);
      setShortName(client.shortName || '');
      setEntityType(client.entityType || '');
      setLogoUrl(client.logoUrl || '');
      setAddressLine1(client.addressLine1 || '');
      setAddressLine2(client.addressLine2 || '');
      setCity(client.city || '');
      setState(client.state || '');
      setZip(client.zip || '');
      setWebsite(client.website || '');
      setIndustry(client.industry || '');
      setNotes(client.notes || '');
      setStatus(client.status);
    } else {
      setName('');
      setShortName('');
      setEntityType('');
      setLogoUrl('');
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setState('');
      setZip('');
      setWebsite('');
      setIndustry('');
      setNotes('');
      setStatus('active');
    }
  }, [client]);

  const handleEnrich = async () => {
    if (!name.trim()) {
      toast.error('Please enter a company name first');
      return;
    }

    setEnriching(true);
    const toastId = toast.loading('✨ Enriching with AI...');

    try {
      const enriched = await clientService.enrichClient(name, {
        shortName,
        entityType,
        website,
        industry,
      });

      // Only update fields that are currently empty
      if (!shortName && enriched.shortName) {
        setShortName(enriched.shortName);
      }
      if (!entityType && enriched.entityType) {
        setEntityType(enriched.entityType);
      }
      if (!website && enriched.website) {
        setWebsite(enriched.website);
      }
      if (!industry && enriched.industry) {
        setIndustry(enriched.industry);
      }
      if (!logoUrl && enriched.logoUrl) {
        setLogoUrl(enriched.logoUrl);
      }

      const fieldsUpdated = [
        !shortName && enriched.shortName && 'Short Name',
        !entityType && enriched.entityType && 'Entity Type',
        !website && enriched.website && 'Website',
        !industry && enriched.industry && 'Industry',
        !logoUrl && enriched.logoUrl && 'Logo',
      ].filter(Boolean);

      if (fieldsUpdated.length > 0) {
        toast.success(`✨ Added: ${fieldsUpdated.join(', ')}`, { id: toastId });
      } else {
        toast('All fields already filled', { id: toastId, icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Failed to enrich client:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to enrich client data';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setEnriching(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      shortName,
      entityType,
      logoUrl,
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      website,
      industry,
      notes,
      status,
    });
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {client ? 'Edit Client' : 'New Client'}
        </h2>
        {/* Temporarily hidden - AI enrichment button */}
        {/* <button
          type="button"
          onClick={handleEnrich}
          disabled={enriching || !name.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          title="Use AI to fill in missing fields"
        >
          <SparklesIcon className={`w-5 h-5 ${enriching ? 'animate-spin' : ''}`} />
          {enriching ? 'Enriching...' : 'Enrich with AI'}
        </button> */}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="shortName" className="block text-sm font-medium text-gray-700 mb-1">
              Short Name
            </label>
            <input
              type="text"
              id="shortName"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              placeholder="e.g., MS, IBM"
              maxLength={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Used for file naming (e.g., "MS_SOW_2025-01-15.docx")
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="entityType" className="block text-sm font-medium text-gray-700 mb-1">
              Entity Type
            </label>
            <input
              type="text"
              id="entityType"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              placeholder="LLC, Corporation, Partnership, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Legal entity type (e.g., LLC, Corporation)</p>
          </div>

          <div>
            <label htmlFor="logoUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Logo URL
            </label>
            <input
              type="url"
              id="logoUrl"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {logoUrl && (
              <div className="mt-2">
                <img 
                  src={logoUrl} 
                  alt="Company logo" 
                  className="h-12 w-12 object-contain border border-gray-200 rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <input
            type="text"
            id="addressLine1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="Street address"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2 (Optional)
          </label>
          <input
            type="text"
            id="addressLine2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="Suite, apt, building, floor, etc."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-3">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <select
              id="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select...</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-1">
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
              ZIP
            </label>
            <input
              type="text"
              id="zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              maxLength={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <select
              id="industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select industry...</option>
              <option value="Technology">Technology</option>
              <option value="Finance">Finance</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Retail">Retail</option>
              <option value="Education">Education</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Consulting">Consulting</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>


        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="active">Active</option>
            <option value="prospect">Prospect</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Additional notes about this client..."
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Client'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Contacts Section - Only show for existing clients */}
      {client && (
        <ContactsList
          clientId={client.id}
          client={client}
          contacts={contacts}
          onReload={reloadContacts}
        />
      )}
    </div>
  );
}

